import TextDropdown from "components/inputs/text-dropdown";
import TextInput from "components/inputs/text-input";
import ModalTemplate from "components/modal/modal-template";
import Radio from "components/radio";
import Typography from "components/typography";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Colors } from "utils/colors";
import { ITrackArtists } from "types/musicData";
import { GetUserArtists } from "services/user/getUser";
import { IDropItem } from "components/inputs/dropdown";

interface IProps {
  close: () => void;
  confirm: (artist: ITrackArtists) => void;
  artistData?: ITrackArtists;
  confirmEdit?: (artist: ITrackArtists) => void;
  excludeArtistIds?: (string | undefined)[];
}
const AddArtistModal = ({
  close,
  confirm,
  artistData,
  confirmEdit,
  excludeArtistIds = []
}: IProps) => {
  const [artistType, setArtistType] = useState("Primary Artist");
  const [artistName, setArtistName] = useState("");
  const [streamingService, setStreamingService] = useState(false);
  const [spotifyUrl, setSpotifyUrl] = useState("");
  const [appleUrl, setAppleUrl] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [inputDisabled, setInputDisabled] = useState(false);
  const [artistId, setArtistId] = useState<string | undefined>(undefined);

  const Artists = () => {
    let temp = GetUserArtists().filter(
      (artist) => !excludeArtistIds?.includes(artist.id)
    );
    let data: (string | IDropItem)[] = ["None"];
    for (let i = 0; i < temp.length; i++) {
      data.push(temp[i].name);
    }
    return data;
  };

  useEffect(() => {
    if (artistData) {
      setArtistType(artistData?.type);
      setArtistName(artistData?.name);

      if (
        artistData.appleUrl ||
        artistData.spotifyUrl ||
        artistData.youtubeUrl
      ) {
        setStreamingService(true);
        setSpotifyUrl(artistData?.spotifyUrl || "");
        setAppleUrl(artistData?.appleUrl || "");
        setYoutubeUrl(artistData?.youtubeUrl || "");
      }
    }
  }, [artistData]);

  const reset = () => {
    setArtistId(undefined);
    setArtistType("Primary Artist");
    setArtistName("");
    setStreamingService(false);
    setSpotifyUrl("");
    setYoutubeUrl("");
    setAppleUrl("");
  };

  const SelectName = (name: string) => {
    setArtistName(name);
    const artistData = GetUserArtists().find((artist) => artist.name === name);
    if (artistData) {
      if (
        artistData.musicPlatforms?.spotify ||
        artistData.musicPlatforms?.appleMusic ||
        artistData.musicPlatforms?.youtube
      ) {
        setStreamingService(true);
      }
      setInputDisabled(true);
      setSpotifyUrl(artistData.musicPlatforms?.spotify || "");
      setAppleUrl(artistData.musicPlatforms?.appleMusic || "");
      setYoutubeUrl(artistData.musicPlatforms?.youtube || "");
      setArtistId(artistData.id);
    } else {
      setInputDisabled(false);
    }
  };
  return (
    <ModalTemplate
      header={artistData ? "Edit Artist" : "Add Artist"}
      confirmText={artistData ? "Edit Artist" : "Add Artist"}
      close={close}
      disableConfirm={artistName.length === 0 || artistName === "None"}
      confirm={() => {
        const artist = {
          type: artistType,
          name: artistName,
          spotifyUrl: spotifyUrl,
          appleUrl: appleUrl,
          youtubeUrl: youtubeUrl
        };

        if (artistData && confirmEdit) {
          confirmEdit({ ...artist, id: artistData.id || undefined });
        } else {
          confirm({ ...artist, id: artistId || undefined });
        }
        close();
        reset();
      }}
    >
      <TextDropdown
        placeholder="Enter artist name or select from plan artists"
        label="Artist Name"
        required
        value={artistName}
        chosenItem={artistName}
        selectItem={(value) => SelectName(value)}
        items={Artists()}
        listCheck={false}
      />

      <ArtistTypeItem style={{ marginTop: "4rem" }}>
        <Radio
          value={artistType === "Primary Artist"}
          onClick={() => setArtistType("Primary Artist")}
        />
        <Typography color={Colors.white200} ml="0.4">
          Primary Artist
        </Typography>
      </ArtistTypeItem>
      <ArtistTypeItem>
        <Radio
          value={artistType === "Featured Artist"}
          onClick={() => setArtistType("Featured Artist")}
        />
        <Typography color={Colors.white200} ml="0.4">
          Featured Artist
        </Typography>
      </ArtistTypeItem>
      <ArtistTypeItem>
        <Radio
          value={artistType === "Remixer"}
          onClick={() => setArtistType("Remixer")}
        />
        <Typography color={Colors.white200} ml="0.4">
          Remixer
        </Typography>
      </ArtistTypeItem>
      <Typography mt="2.4" weight="medium" mb="1.6">
        Does this artist have music live on streaming services
      </Typography>
      <StreamingServiceRadioContainer>
        <Radio
          value={streamingService}
          onClick={() => setStreamingService(true)}
        />
        <Typography color={Colors.white200} ml="0.4" mr="5">
          Yes
        </Typography>
        <Radio
          value={!streamingService}
          onClick={() => setStreamingService(false)}
        />
        <Typography color={Colors.white200} ml="0.4">
          No
        </Typography>
      </StreamingServiceRadioContainer>
      {streamingService && (
        <>
          <StreamingServiceItem>
            <TextInput
              label="Spotify Artist URL"
              placeholder="Enter Spotify Artist Link"
              value={spotifyUrl}
              onChange={(e) => setSpotifyUrl(e.target.value)}
              disabled={inputDisabled}
            />
          </StreamingServiceItem>
          <StreamingServiceItem>
            <TextInput
              label="Apple Artist URL"
              placeholder="Enter Apple Music Artist Link"
              value={appleUrl}
              onChange={(e) => setAppleUrl(e.target.value)}
              disabled={inputDisabled}
            />
          </StreamingServiceItem>
          <StreamingServiceItem>
            <TextInput
              label="YouTube Music Artist URL"
              placeholder="Enter YouTube Music Artist Link"
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              disabled={inputDisabled}
            />
          </StreamingServiceItem>
        </>
      )}
    </ModalTemplate>
  );
};

export default AddArtistModal;

const ArtistTypeItem = styled.div`
  display: flex;
  align-items: center;
  margin-top: 2.4rem;
`;

const StreamingServiceRadioContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StreamingServiceItem = styled.div`
  margin-top: 1.6rem;
`;
